import { useCallback, useEffect, useRef, useState } from "react";
import Head from "next/head";
import { cloneDeep } from "lodash";
import Page from "../layouts/main";
import Context from "../components/context/context";
import Footer from "../components/Footer/footer.module";
import Picture from "../components/picture/picture.module";
import InputField from "../components/Form/InputField/inputfield.module";
import TextArea from "../components/Form/TextArea/textarea.module";
import styles from "../styles/modules/business-opportunities.module.scss";
import settings from "../util/settings";
import { useMemo, useContext } from "react";
import { DEFAULT_LOCATION } from "../util/locations";
import { trackBusinessOpportunitiesFormEvent } from "../util/conversion-events/contact-form-events";

const ASSET_PATH = settings.ASSET_PATH + "images/business-opportunities/";

const BusinessOpportunities = () => {
  const { location } = useContext(Context);
  const locationToUse = useMemo(
    () => (location ? location : DEFAULT_LOCATION),
    [location]
  );

  const [showError, setShowError] = useState({
    fname: false,
    lname: false,
    email: false,
    phone: false,
    subject: false,
    message: false,
  });
  const [inputValues, setInputValues] = useState({
    subject: "Business Opportunities",
  });
  const [emailState, setEmailState] = useState({
    submitted: false,
    sent: false,
    failed: false,
  });
  const [analytics, setAnalytics] = useState({});

  const page = useRef();
  const inputs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (page.current) {
      setAnalytics(page.current.analytics);
    }
  }, []);

  const handleChange = useCallback(
    (e, input, value) => {
      setInputValues({ ...inputValues, [input.props.id]: value });
      setShowError({ ...showError, [input.props.id]: true });
    },
    [inputValues, showError]
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      for (let i = 0; i < inputs.length; i++) {
        if (inputs[i].current.state && !inputs[i].current.state.valid) {
          let showErrorClone = { ...showError };
          Object.keys(showErrorClone).forEach((key) => {
            showErrorClone[key] = true;
          });
          setShowError(showErrorClone);
          return;
        }
      }

      let emailStateClone = cloneDeep(emailState);
      emailStateClone.failed = false;
      emailStateClone.submitted = true;
      setEmailState(emailStateClone);

      const formData = new URLSearchParams();
      for (var k in inputValues) {
        formData.append(k, inputValues[k]);
      }

      fetch("/forms/contact-form.php", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-type": "application/x-www-form-urlencoded",
        },
        body: formData,
      })
        .then((response) => {
          if (!response.ok) {
            emailStateClone.failed = true;
          } else {
            emailStateClone.sent = true;
          }
          return response.json();
        })
        .then((data) => {
          // if (settings.isDev) {
          //   console.log(data)
          //   document.open();
          //   document.write(data);
          // }
          trackCTA(true);
          trackBusinessOpportunitiesFormEvent();
          setTimeout(() => setEmailState({ ...emailStateClone }), 500);
        })
        .catch((msg) => {
          trackCTA(false);
          emailStateClone.failed = true;
          setTimeout(() => setEmailState({ ...emailStateClone }), 2000);
        });
    },
    [emailState, showError, inputValues, analytics]
  );

  const trackCTA = useCallback(
    (successful) => {
      analytics.event({
        category: "business-opportunities-form",
        action: "form-submission",
        label: successful ? "successful" : "failed",
      });
    },
    [analytics]
  );

  return (
    <Page id={styles["page-business-opportunities"]} ref={page}>
      <Head>
        <title>Business Opportunities - Kudos Golf</title>
      </Head>
      <section className={`section-content ${styles["section-biz-opps"]}`}>
        <h2 className={styles.header}>
          Shaping the future of golf through technology, realism, and
          performance
        </h2>

        <h3
          className={styles["section-biz-opps-heading"]}
          style={{ marginBottom: "2rem" }}
        >
          Kudos Golf Business Opportunities
        </h3>

        <div className={styles["section-biz-opps-desc"]}>
          <p className="text-body-medium bold">
            Are you interested in starting a turnkey, sophisticated and
            fully-automated virtual golfing business? Generate income 24/7 with
            our advanced e-commerce platform, golf simulator technology, and
            best customer experience!
          </p>

          <p className="text-body-medium">
            Our Simulators are the most comprehensive indoor tracking system
            available to accurately track golf shots offering highest-quality,
            most lifelike and customizable golf simulator software ever created.
          </p>

          <p className="text-body-medium">
            We will consult with you in many aspects of the business from
            business operations, design and layout, staff and technology
            training, marketing plans, and will build a fully functioning
            e-commerce website.
          </p>
        </div>
      </section>

      <section className={`row section-content ${styles["section-location"]}`}>
        <div className={`col-md-6 ${styles['section-location-container-1']}`}>
          <Picture
            src={`${ASSET_PATH}vaughan.jpg`}
            alt={"Kudos Golf Vaughan location image"}
            className={styles["section-location-image"]}
          />
        </div>
        <div className={`col-md-6 ${styles['section-location-container-2']}`}>
          <Picture
            src={`${ASSET_PATH}saskatoon.jpg`}
            alt={"Kudos Golf Saskatoon location image"}
            className={styles["section-location-image"]}
          />
        </div>
      </section>

      <section className={`row section-content ${styles["section-form"]}`}>
        <h3 className={styles["section-biz-opps-heading"]}>
          Let’s bring your vision to reality
        </h3>

        <p className="text-body-medium">
          For booking inquiries and troubleshooting, please contact the facility
          at{" "}
          <a href={`mailto:${locationToUse.email}`} className="text-link">
            {locationToUse.email}
          </a>
        </p>

        {(!emailState.submitted ||
          (emailState.submitted && !emailState.sent)) && (
          <form onSubmit={handleSubmit} autoComplete="true">
            <div className="row">
              <div className="col-md-4 mt-3">
                <InputField
                  autoComplete="given-name"
                  maxLength={200}
                  ref={inputs[0]}
                  id="fname"
                  name="fname"
                  label="First Name"
                  type="text"
                  isRequired
                  value={inputValues.fname}
                  placeholder="John"
                  showError={showError.fname}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 mt-3">
                <InputField
                  autoComplete="family-name"
                  maxLength={200}
                  ref={inputs[1]}
                  id="lname"
                  label="Last Name"
                  type="text"
                  isRequired
                  value={inputValues.lname}
                  placeholder="Smith"
                  showError={showError.lname}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 mt-3">
                <InputField
                  autoComplete="email"
                  maxLength={200}
                  ref={inputs[2]}
                  id="email"
                  label="E-mail"
                  type="email"
                  isRequired
                  value={inputValues.email}
                  placeholder="johnsmith@gmail.com"
                  showError={showError.email}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 mt-3">
                <InputField
                  autoComplete="tel"
                  maxLength={200}
                  ref={inputs[3]}
                  id="phone"
                  label="Phone"
                  type="phone"
                  value={inputValues.phone}
                  placeholder="(123) 456 - 7890"
                  showError={showError.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row" style={{ display: 'none' }}>
              <div className="col-md-8 mt-3">
                <InputField
                  maxLength={200}
                  ref={inputs[4]}
                  id="subject"
                  label="Subject"
                  type="text"
                  disabled
                  isRequired
                  value={inputValues.subject}
                  placeholder="e.g. Booking inquiry"
                  showError={showError.subject}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-8 mt-3">
                <TextArea
                  maxLength={500}
                  ref={inputs[5]}
                  id="message"
                  label="Message"
                  isRequired
                  value={inputValues.subject}
                  placeholder="Your inquiry here"
                  showError={showError.message}
                  onChange={handleChange}
                />
              </div>
            </div>

            {(!emailState.submitted ||
              (emailState.submitted && emailState.failed)) && (
              <div className="mt-4">
                <button className="button" type="submit">
                  Send message
                </button>
              </div>
            )}
          </form>
        )}
        {emailState.submitted && (
          <div className="mt-5">
            {emailState.sent && (
              <>
                <p className="mb-0">
                  <strong>Thank you for your message!</strong>
                </p>
                <p>
                  A member of our team will be in touch with you as soon as
                  possible.
                </p>
              </>
            )}
            {emailState.failed && (
              <p>
                Sorry, there was an error sending your email. Please try again
                later.
              </p>
            )}
            {!emailState.sent && !emailState.failed && <p>Sending email...</p>}
          </div>
        )}
      </section>
      <Footer analytics={analytics} />
    </Page>
  );
};

export default BusinessOpportunities;
